import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Grid, TextField, Button,
    Card, CardContent, CardHeader, FormControlLabel, List, ListItem, ListItemIcon,
    ListItemText, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Snackbar, Alert, Switch, Autocomplete, IconButton,
    Radio, RadioGroup, FormControl, FormLabel
} from '@mui/material';
import {
    FileDownload, Analytics,  Add, Remove, Assessment, ArrowUpward, ArrowDownward, Download
} from '@mui/icons-material';
import { useEvent } from '../../contexts/event.context';
import api from '../../services/api';
import * as qs from 'qs';
import XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';

// Campos disponíveis para relatórios
const AVAILABLE_FIELDS = {
    // Dados do Pedido
    order: {
        title: 'Dados do Pedido',
        fields: {
            external_id: { label: 'Número da Inscrição', type: 'text' },
            transaction: { label: 'ID da Transação', type: 'text' },
            status: { label: 'Status do Pagamento', type: 'text' },
            payment_form: { label: 'Forma de Pagamento', type: 'text' },
            total: { label: 'Valor Total', type: 'currency' },
            quantity: { label: 'Quantidade de Inscritos', type: 'number' },
            createdAt: { label: 'Data da Inscrição', type: 'datetime' },
            updatedAt: { label: 'Data de Atualização', type: 'datetime' },
            paymentDate: { label: 'Data de Pagamento', type: 'datetime' },
            due_date: { label: 'Data de Vencimento', type: 'date' }
        }
    },
    // Dados do Evento
    event: {
        title: 'Dados do Evento',
        fields: {
            event_name: { label: 'Nome do Evento', type: 'text' },
            event_start_date: { label: 'Data de Início do Evento', type: 'date' },
            event_end_date: { label: 'Data de Fim do Evento', type: 'date' },
            event_location: { label: 'Local do Evento', type: 'text' },
            plan_type: { label: 'Tipo de Inscrição', type: 'text' },
            plan_price: { label: 'Preço do Plano', type: 'currency' },
            event_max_subscribers: { label: 'Máximo de Inscritos', type: 'number' },
            event_count_subscribers: { label: 'Total de Inscritos', type: 'number' }
        }
    },
    // Dados da Empresa
    company: {
        title: 'Dados da Empresa',
        fields: {
            company_document: { label: 'CNPJ', type: 'text' },
            company_social_reason: { label: 'Razão Social', type: 'text' },
            company_trade: { label: 'Nome Fantasia', type: 'text' },
            company_email: { label: 'Email da Empresa', type: 'email' },
            company_phone: { label: 'Telefone da Empresa', type: 'text' },
            company_address: { label: 'Endereço', type: 'text' },
            company_city: { label: 'Cidade', type: 'text' },
            company_state: { label: 'Estado', type: 'text' },
            company_cep: { label: 'CEP', type: 'text' },
            company_district: { label: 'Bairro', type: 'text' },
            company_number: { label: 'Número', type: 'text' }
        }
    },
    // Dados do Inscrito
    subscriber: {
        title: 'Dados do Inscrito',
        fields: {
            subscriber_document: { label: 'CPF', type: 'text' },
            subscriber_name: { label: 'Nome Completo', type: 'text' },
            subscriber_email: { label: 'Email', type: 'email' },
            subscriber_whatsapp: { label: 'WhatsApp', type: 'text' },
            subscriber_phone: { label: 'Telefone', type: 'text' },
            subscriber_birth: { label: 'Data de Nascimento', type: 'date' },
            subscriber_gender: { label: 'Gênero', type: 'text' },
            subscriber_office: { label: 'Cargo', type: 'text' },
            subscriber_badge: { label: 'Crachá', type: 'text' },
            subscriber_tshirtSize: { label: 'Tamanho da Camisa', type: 'text' },
            subscriber_state: { label: 'Estado (Inscrito)', type: 'text' },
            subscriber_city: { label: 'Cidade (Inscrito)', type: 'text' },
            subscriber_isSubstitute: { label: 'É Substituto', type: 'boolean' },
            subscriber_presence: { label: 'Presença', type: 'boolean' },
            subscriber_groups: { label: 'Grupos', type: 'array' }
        }
    },
    // Dados de Hospedagem
    hosting: {
        title: 'Dados de Hospedagem',
        fields: {
            hosting_name: { label: 'Nome do Hotel', type: 'text' },
            hosting_type: { label: 'Tipo de Hospedagem', type: 'text' },
            hosting_room: { label: 'Quarto', type: 'text' },
            checkin: { label: 'Data de Check-in', type: 'date' },
            checkout: { label: 'Data de Check-out', type: 'date' }
        }
    }
};

export default function Relatorios() {
    const [selectedFields, setSelectedFields] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [reportName, setReportName] = useState('');
    const [generating, setGenerating] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });
    const [birthdayPeriod, setBirthdayPeriod] = useState('MENSAL'); // DIARIA, SEMANAL, MENSAL
    const [birthdayDate, setBirthdayDate] = useState(new Date().toISOString().split('T')[0]);
    const [birthdayEvent, setBirthdayEvent] = useState(null); // Evento opcional para aniversariantes
    const [templates, setTemplates] = useState({
        'inscricoes_completo': {
            name: 'Relatório Completo de Inscrições',
            fields: [
                { id: 'order_external_id', category: 'order', key: 'external_id', label: 'Número da Inscrição', type: 'text', enabled: true },
                { id: 'event_event_name', category: 'event', key: 'event_name', label: 'Nome do Evento', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_name', category: 'subscriber', key: 'subscriber_name', label: 'Nome Completo', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_document', category: 'subscriber', key: 'subscriber_document', label: 'CPF', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_email', category: 'subscriber', key: 'subscriber_email', label: 'Email', type: 'email', enabled: true },
                { id: 'company_company_social_reason', category: 'company', key: 'company_social_reason', label: 'Razão Social', type: 'text', enabled: true },
                { id: 'order_status', category: 'order', key: 'status', label: 'Status do Pagamento', type: 'text', enabled: true },
                { id: 'order_createdAt', category: 'order', key: 'createdAt', label: 'Data da Inscrição', type: 'datetime', enabled: true }
            ]
        },
        'financeiro': {
            name: 'Relatório Financeiro',
            fields: [
                { id: 'order_external_id', category: 'order', key: 'external_id', label: 'Número da Inscrição', type: 'text', enabled: true },
                { id: 'event_event_name', category: 'event', key: 'event_name', label: 'Nome do Evento', type: 'text', enabled: true },
                { id: 'order_status', category: 'order', key: 'status', label: 'Status do Pagamento', type: 'text', enabled: true },
                { id: 'order_payment_form', category: 'order', key: 'payment_form', label: 'Forma de Pagamento', type: 'text', enabled: true },
                { id: 'order_total', category: 'order', key: 'total', label: 'Valor Total', type: 'currency', enabled: true },
                { id: 'order_quantity', category: 'order', key: 'quantity', label: 'Quantidade de Inscritos', type: 'number', enabled: true },
                { id: 'company_company_social_reason', category: 'company', key: 'company_social_reason', label: 'Razão Social', type: 'text', enabled: true },
                { id: 'order_createdAt', category: 'order', key: 'createdAt', label: 'Data da Inscrição', type: 'datetime', enabled: true }
            ]
        },
        'presenca': {
            name: 'Relatório de Presença',
            fields: [
                { id: 'event_event_name', category: 'event', key: 'event_name', label: 'Nome do Evento', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_name', category: 'subscriber', key: 'subscriber_name', label: 'Nome Completo', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_document', category: 'subscriber', key: 'subscriber_document', label: 'CPF', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_email', category: 'subscriber', key: 'subscriber_email', label: 'Email', type: 'email', enabled: true },
                { id: 'subscriber_subscriber_whatsapp', category: 'subscriber', key: 'subscriber_whatsapp', label: 'WhatsApp', type: 'text', enabled: true },
                { id: 'company_company_social_reason', category: 'company', key: 'company_social_reason', label: 'Razão Social', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_presence', category: 'subscriber', key: 'subscriber_presence', label: 'Presença', type: 'boolean', enabled: true }
            ]
        },
        'inscritos_unicos': {
            name: 'Inscritos Únicos - Campos Predefinidos',
            fields: [
                { id: 'subscriber_subscriber_state', category: 'subscriber', key: 'subscriber_state', label: 'UF', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_city', category: 'subscriber', key: 'subscriber_city', label: 'Cidade', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_name', category: 'subscriber', key: 'subscriber_name', label: 'Nome', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_gender', category: 'subscriber', key: 'subscriber_gender', label: 'Sexo', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_birth', category: 'subscriber', key: 'subscriber_birth', label: 'Data Nascimento', type: 'date', enabled: true },
                { id: 'subscriber_subscriber_email', category: 'subscriber', key: 'subscriber_email', label: 'Email', type: 'email', enabled: true },
                { id: 'subscriber_subscriber_whatsapp', category: 'subscriber', key: 'subscriber_whatsapp', label: 'Celular', type: 'text', enabled: true }
            ]
        }
    });

    const { events } = useEvent();
    const [eventOptions, setEventOptions] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [hasMoreEvents, setHasMoreEvents] = useState(true);
    const [eventSearchText, setEventSearchText] = useState('');
    const pageEvents = useRef(1);
    const eventIdsRef = useRef(new Set());
    const eventSearchTimer = useRef(null);
    const listboxRef = useRef(null);
    const scrollPositionRef = useRef(0);

    const statusOptions = [
        'PENDENTE', 'AGUARDANDO PAGAMENTO', 'PAGO', 'CORTESIA',
        'EMPENHO', 'CANCELADA', 'ATRASADA', 'RECLAMADA', 'REEMBOLSADA'
    ];

    const formatEventOption = (event) => ({
        ...event,
        attributes: {
            ...event.attributes,
            end_subscription: event.attributes?.end_subscription
                ? format(parseISO(event.attributes.end_subscription), 'dd/MM/yy')
                : event.attributes?.end_subscription,
            date_initial: event.attributes?.date_initial
                ? format(parseISO(event.attributes.date_initial), 'dd/MM/yy')
                : event.attributes?.date_initial,
            count_subscribers: `${event.attributes?.count_subscribers ?? ''}`,
            isActive: event.attributes?.isActive === true ? 'Aberto' : 'Encerrado'
        }
    });

    async function fetchEventsPage(page = 1, searchText = '') {
        const normalizedSearch = searchText.trim();
        const query = qs.stringify({
            filters: {
                deleted: { $eq: false },
                ...(normalizedSearch && {
                    name: { $containsi: normalizedSearch }
                })
            },
            pagination: { page, pageSize: 20 },
            fields: ['name', 'city', 'count_subscribers', 'max_subscribers', 'date_initial', 'end_subscription', 'isActive', 'counter_hostings', 'typeForm'],
            populate: {
                plans: { populate: '*' },
                hostings: { populate: '*' },
                collectors: { populate: '*' }
            },
            sort: ['createdAt:desc'],
            encodeValuesOnly: true,
        });

        const response = await api.get(`/events?${query}`);

        return {
            events: response.data.data.map(formatEventOption),
            pageCount: response.data.meta?.pagination?.pageCount || 0
        };
    }

    async function loadMoreEvents() {
        if (!hasMoreEvents || loadingEvents) return;

        setLoadingEvents(true);
        try {
            const nextPage = pageEvents.current + 1;
            const { events: nextEvents, pageCount } = await fetchEventsPage(nextPage, eventSearchText);

            if (nextEvents.length > 0) {
                nextEvents.forEach(event => eventIdsRef.current.add(event.id));
                setEventOptions(prev => {
                    const existingIds = new Set(prev.map(item => item.id));
                    const filtered = nextEvents.filter(item => !existingIds.has(item.id));
                    return [...prev, ...filtered];
                });
            }

            pageEvents.current = nextPage;
            setHasMoreEvents(nextPage < pageCount);
        } catch (error) {
            console.error('Erro ao carregar eventos:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    async function loadInitialEvents() {
        setLoadingEvents(true);
        try {
            const { events: initialEvents, pageCount } = await fetchEventsPage(1);

            pageEvents.current = 1;
            eventIdsRef.current.clear();

            initialEvents.forEach(event => eventIdsRef.current.add(event.id));
            setEventOptions(initialEvents);
            setHasMoreEvents(pageCount > 1);
        } catch (error) {
            console.error('Erro ao carregar eventos iniciais:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    async function searchEventsByText(searchText) {
        if (!searchText || searchText.trim().length < 2) {
            loadInitialEvents();
            return;
        }

        setLoadingEvents(true);
        try {
            const { events: searchedEvents, pageCount } = await fetchEventsPage(1, searchText);

            eventIdsRef.current.clear();
            searchedEvents.forEach(event => eventIdsRef.current.add(event.id));
            setEventOptions(searchedEvents);
            pageEvents.current = 1;
            setHasMoreEvents(pageCount > 1);
        } catch (error) {
            console.error('Erro ao buscar eventos:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    useEffect(() => {
        loadInitialEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (events && events.length > 0 && eventOptions.length === 0) {
            const newEvents = events.filter(event => !eventIdsRef.current.has(event.id));
            if (newEvents.length > 0) {
                newEvents.forEach(event => eventIdsRef.current.add(event.id));
                setEventOptions(prev => {
                    const existingIds = new Set(prev.map(e => e.id));
                    const filtered = newEvents.filter(e => !existingIds.has(e.id));
                    return [...prev, ...filtered];
                });
            }
        }
    }, [eventOptions.length, events]);

    useEffect(() => {
        return () => {
            if (eventSearchTimer.current) {
                clearTimeout(eventSearchTimer.current);
            }
        };
    }, []);

    // Função para carregar template
    const handleLoadTemplate = (templateKey) => {
        const template = templates[templateKey];
        if (template) {
            setSelectedFields(template.fields);
            setReportName(template.name);
            setStateAlert({
                status: true,
                type: 'success',
                msg: `Template "${template.name}" carregado com sucesso!`
            });
        }
    };

    // Função para limpar campos
    const handleClearFields = () => {
        setSelectedFields([]);
        setReportName('');
    };

    // Função para adicionar campo
    const handleAddField = (categoryKey, fieldKey) => {
        const field = AVAILABLE_FIELDS[categoryKey].fields[fieldKey];
        const newField = {
            id: `${categoryKey}_${fieldKey}`,
            category: categoryKey,
            key: fieldKey,
            label: field.label,
            type: field.type,
            enabled: true
        };

        if (!selectedFields.find(f => f.id === newField.id)) {
            setSelectedFields([...selectedFields, newField]);
        }
    };

    // Função para remover campo
    const handleRemoveField = (fieldId) => {
        setSelectedFields(selectedFields.filter(f => f.id !== fieldId));
    };

    // Função para mover campo para cima
    const handleMoveUp = (index) => {
        if (index === 0) return;
        const newFields = [...selectedFields];
        [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
        setSelectedFields(newFields);
    };

    // Função para mover campo para baixo
    const handleMoveDown = (index) => {
        if (index === selectedFields.length - 1) return;
        const newFields = [...selectedFields];
        [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
        setSelectedFields(newFields);
    };

    // Função para reordenar campos (substituindo drag and drop)
    const handleDragEnd = (result) => {
        // Mantemos essa função vazia para compatibilidade
        return;
    };

    // Função para toggle de campo
    const handleToggleField = (fieldId) => {
        setSelectedFields(selectedFields.map(field =>
            field.id === fieldId
                ? { ...field, enabled: !field.enabled }
                : field
        ));
    };

    // Função para buscar dados do relatório
    const fetchReportData = async () => {
        try {
            let page = 1;
            const pageSize = 500;
            let allOrders = [];

            while (true) {
                const params = {
                    sort: ['createdAt:desc'],
                    pagination: { page, pageSize },
                    filters: {
                        deleted: { $eq: false },
                        ...(selectedEvents.length > 0 && {
                            event: { name: { $in: selectedEvents.map(e => e.attributes?.name).filter(Boolean) } }
                        }),
                        ...(selectedStatus.length > 0 && {
                            status: { $in: selectedStatus }
                        }),
                        ...(dateRange.start && {
                            createdAt: { $gte: dateRange.start }
                        }),
                        ...(dateRange.end && {
                            createdAt: { $lte: dateRange.end }
                        })
                    },
                    populate: {
                        event: { fields: ['name'] },
                        plan: { fields: ['type', 'price'] },
                        company: { fields: ['*'] },
                        subscribers: { fields: ['*'] },
                        hosting: { fields: ['*'] }
                    },
                    encodeValuesOnly: true
                };

                const queryString = qs.stringify(params, {
                    skipNulls: false,
                    skipUndefined: false,
                    skipEmptyStrings: true
                });

                const response = await api.get(`/orders?${queryString}`);
                const ordersData = response.data.data;

                if (!ordersData || ordersData.length === 0) break;

                allOrders.push(...ordersData);
                page++;
            }

            return allOrders;
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
            throw error;
        }
    };

    // Função para transformar dados de inscritos únicos (sem repetição)
    const transformUniqueSubscribersData = (orders, fieldsToUse = null) => {
        const enabledFields = (fieldsToUse || selectedFields).filter(f => f.enabled);
        const uniqueSubscribers = new Map(); // Usando Map para evitar duplicatas por CPF

        orders.forEach(order => {
            const subscribers = order.attributes.subscribers?.data || [];
            
            subscribers.forEach(subscriber => {
                const cpf = subscriber.attributes?.document;
                if (cpf && !uniqueSubscribers.has(cpf)) {
                    const row = {};
                    
                    enabledFields.forEach(field => {
                        const value = getFieldValue(order, subscriber, field);
                        row[field.label] = formatFieldValue(value, field.type);
                    });
                    
                    uniqueSubscribers.set(cpf, row);
                }
            });
        });

        return Array.from(uniqueSubscribers.values());
    };

    // Função para transformar dados conforme campos selecionados
    const transformData = (orders) => {
        const enabledFields = selectedFields.filter(f => f.enabled);

        return orders.flatMap(order => {
            const subscribers = order.attributes.subscribers?.data || [{}];

            return subscribers.map(subscriber => {
                const row = {};

                enabledFields.forEach(field => {
                    const value = getFieldValue(order, subscriber, field);
                    row[field.label] = formatFieldValue(value, field.type);
                });

                return row;
            });
        });
    };

    // Função para obter valor do campo
    const getFieldValue = (order, subscriber, field) => {
        const { category, key } = field;

        switch (category) {
            case 'order':
                switch (key) {
                    case 'external_id':
                        return order.attributes.external_id;
                    case 'transaction':
                        return order.attributes.transaction;
                    case 'status':
                        return order.attributes.status;
                    case 'payment_form':
                        return order.attributes.payment_form;
                    case 'total':
                        return order.attributes.total;
                    case 'quantity':
                        return order.attributes.quantity;
                    case 'createdAt':
                        return order.attributes.createdAt;
                    case 'updatedAt':
                        return order.attributes.updatedAt;
                    case 'paymentDate':
                        return order.attributes.paymentDate;
                    case 'due_date':
                        return order.attributes.due_date;
                    default:
                        return '';
                }

            case 'event':
                const event = order.attributes.event?.data?.attributes;
                const plan = order.attributes.plan?.data?.attributes;
                switch (key) {
                    case 'event_name':
                        return event?.name;
                    case 'event_start_date':
                        return event?.start_date;
                    case 'event_end_date':
                        return event?.end_date;
                    case 'event_location':
                        return event?.location;
                    case 'plan_type':
                        return plan?.type;
                    case 'plan_price':
                        return plan?.price;
                    case 'event_max_subscribers':
                        return event?.max_subscribers;
                    case 'event_count_subscribers':
                        return event?.count_subscribers;
                    default:
                        return '';
                }

            case 'company':
                const company = order.attributes.company?.data?.attributes;
                switch (key) {
                    case 'company_document':
                        return company?.document;
                    case 'company_social_reason':
                        return company?.social_reason;
                    case 'company_trade':
                        return company?.trade;
                    case 'company_email':
                        return company?.email;
                    case 'company_phone':
                        return company?.phone;
                    case 'company_address':
                        return company?.address;
                    case 'company_city':
                        // Se não há company, busca do subscriber
                        return company?.city || subscriber?.attributes?.city;
                    case 'company_state':
                        // Se não há company, busca do subscriber
                        return company?.state || subscriber?.attributes?.state;
                    case 'company_cep':
                        return company?.cep;
                    case 'company_district':
                        return company?.district;
                    case 'company_number':
                        return company?.number;
                    default:
                        return '';
                }

            case 'subscriber':
                const sub = subscriber?.attributes;
                switch (key) {
                    case 'subscriber_document':
                        return sub?.document;
                    case 'subscriber_name':
                        return sub?.name;
                    case 'subscriber_email':
                        return sub?.email;
                    case 'subscriber_whatsapp':
                        return sub?.whatsapp;
                    case 'subscriber_phone':
                        return sub?.phone;
                    case 'subscriber_birth':
                        return sub?.birth;
                    case 'subscriber_gender':
                        return sub?.gender;
                    case 'subscriber_office':
                        return sub?.office;
                    case 'subscriber_badge':
                        return sub?.badge;
                    case 'subscriber_tshirtSize':
                        return sub?.tshirtSize;
                    case 'subscriber_state':
                        console.log('Debug subscriber_state:', sub?.state, 'subscriber:', sub);
                        return sub?.state;
                    case 'subscriber_city':
                        console.log('Debug subscriber_city:', sub?.city, 'subscriber:', sub);
                        return sub?.city;
                    case 'subscriber_isSubstitute':
                        return !!sub?.isSubstitute;
                    case 'subscriber_presence':
                        return sub?.presence;
                    case 'subscriber_groups':
                        return sub?.groups;
                    default:
                        return '';
                }

            case 'hosting':
                const hosting = order.attributes.hosting?.data?.attributes || subscriber?.attributes?.hosting;
                switch (key) {
                    case 'hosting_name':
                        return hosting?.name;
                    case 'hosting_type':
                        return hosting?.type;
                    case 'hosting_room':
                        return hosting?.room;
                    case 'checkin':
                        return order.attributes.checkin;
                    case 'checkout':
                        return order.attributes.checkout;
                    default:
                        return '';
                }

            default:
                return '';
        }
    };

    // Função para formatar valor conforme tipo
    const formatFieldValue = (value, type) => {
        if (!value && value !== 0 && value !== false) return '';

        switch (type) {
            case 'datetime':
                try {
                    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
                } catch {
                    return value;
                }
            case 'date':
                try {
                    return format(parseISO(value), 'dd/MM/yyyy');
                } catch {
                    return value;
                }
            case 'currency':
                return Number(value).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                });
            case 'boolean':
                return value ? 'Sim' : 'Não';
            case 'array':
                return Array.isArray(value) ? value.join(', ') : value;
            default:
                return String(value);
        }
    };

    // Função para preview dos dados
    const handlePreview = async () => {
        if (selectedFields.filter(f => f.enabled).length === 0) {
            setStateAlert({
                status: true,
                type: 'warning',
                msg: 'Selecione pelo menos um campo para o relatório!'
            });
            return;
        }

        try {
            setGenerating(true);
            const orders = await fetchReportData();
            
            // Verifica se é o template de inscritos únicos
            const isUniqueSubscribersReport = reportName.includes('Inscritos Únicos') || 
                                               selectedFields.some(f => f.id === 'subscriber_subscriber_state');
            
            const transformedData = isUniqueSubscribersReport 
                ? transformUniqueSubscribersData(orders, selectedFields)
                : transformData(orders);
            
            setPreviewData(transformedData.slice(0, 10)); // Mostra apenas primeiras 10 linhas
            setPreviewDialog(true);
        } catch (error) {
            setStateAlert({
                status: true,
                type: 'error',
                msg: 'Erro ao gerar preview: ' + error.message
            });
        } finally {
            setGenerating(false);
        }
    };

    // Função para gerar Excel
    const handleGenerateExcel = async () => {
        if (selectedFields.filter(f => f.enabled).length === 0) {
            setStateAlert({
                status: true,
                type: 'warning',
                msg: 'Selecione pelo menos um campo para o relatório!'
            });
            return;
        }

        try {
            setGenerating(true);
            const orders = await fetchReportData();
            
            // Verifica se é o template de inscritos únicos
            const isUniqueSubscribersReport = reportName.includes('Inscritos Únicos') || 
                                               selectedFields.some(f => f.id === 'subscriber_subscriber_state');
            
            const transformedData = isUniqueSubscribersReport 
                ? transformUniqueSubscribersData(orders, selectedFields)
                : transformData(orders);

            if (transformedData.length === 0) {
                setStateAlert({
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum dado encontrado com os filtros aplicados!'
                });
                return;
            }

            // Gera Excel
            const sheet = XLSX.utils.json_to_sheet(transformedData);

            // Define larguras das colunas baseado no tipo de campo
            const enabledFields = selectedFields.filter(f => f.enabled);
            const columnWidths = enabledFields.map(field => {
                switch (field.type) {
                    case 'datetime': return { wch: 18 };
                    case 'date': return { wch: 12 };
                    case 'currency': return { wch: 15 };
                    case 'email': return { wch: 30 };
                    case 'text': return { wch: 20 };
                    default: return { wch: 15 };
                }
            });

            sheet['!cols'] = columnWidths;

            const workBook = XLSX.utils.book_new();
            let sheetName = reportName || 'Relatório Personalizado';
            // Excel sheet names têm limite de 31 caracteres
            if (sheetName.length > 31) {
                sheetName = sheetName.substring(0, 28) + '...';
            }
            XLSX.utils.book_append_sheet(workBook, sheet, sheetName);

            const fileName = `${reportName || 'relatorio'}-${new Date().toISOString().split('T')[0]}.xlsx`;
            await XLSX.writeFile(workBook, fileName);

            const recordType = isUniqueSubscribersReport ? 'inscritos únicos' : 'registros';
            setStateAlert({
                status: true,
                type: 'success',
                msg: `Relatório gerado com sucesso! ${transformedData.length} ${recordType} exportados.`
            });

        } catch (error) {
            setStateAlert({
                status: true,
                type: 'error',
                msg: 'Erro ao gerar relatório: ' + error.message
            });
        } finally {
            setGenerating(false);
        }
    };

    // Função específica para gerar relatório de inscritos únicos rapidamente
    const handleGenerateUniqueSubscribersReport = async () => {
        try {
            setGenerating(true);
            
            // Define automaticamente os campos predefinidos
            const predefinedFields = [
                { id: 'subscriber_subscriber_state', category: 'subscriber', key: 'subscriber_state', label: 'UF', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_city', category: 'subscriber', key: 'subscriber_city', label: 'Cidade', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_name', category: 'subscriber', key: 'subscriber_name', label: 'Nome', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_gender', category: 'subscriber', key: 'subscriber_gender', label: 'Sexo', type: 'text', enabled: true },
                { id: 'subscriber_subscriber_birth', category: 'subscriber', key: 'subscriber_birth', label: 'Data Nascimento', type: 'date', enabled: true },
                { id: 'subscriber_subscriber_email', category: 'subscriber', key: 'subscriber_email', label: 'Email', type: 'email', enabled: true },
                { id: 'subscriber_subscriber_whatsapp', category: 'subscriber', key: 'subscriber_whatsapp', label: 'Celular', type: 'text', enabled: true }
            ];
            
            // Define temporariamente os campos
            const originalFields = selectedFields;
            
            const orders = await fetchReportData();
            const transformedData = transformUniqueSubscribersData(orders, predefinedFields);

            if (transformedData.length === 0) {
                setStateAlert({
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum inscrito encontrado com os filtros aplicados!'
                });
                return;
            }

            // Gera Excel
            const sheet = XLSX.utils.json_to_sheet(transformedData);
            
            // Define larguras das colunas otimizadas para os campos predefinidos
            const columnWidths = [
                { wch: 6 },  // UF
                { wch: 20 }, // Cidade
                { wch: 30 }, // Nome
                { wch: 8 },  // Sexo
                { wch: 15 }, // Data Nascimento
                { wch: 30 }, // Email
                { wch: 18 }  // Celular
            ];
            
            sheet['!cols'] = columnWidths;

            const workBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workBook, sheet, 'Inscritos Únicos');

            const fileName = `inscritos-unicos-${new Date().toISOString().split('T')[0]}.xlsx`;
            await XLSX.writeFile(workBook, fileName);

            setStateAlert({
                status: true,
                type: 'success',
                msg: `Relatório de inscritos únicos gerado! ${transformedData.length} inscritos únicos exportados.`
            });

        } catch (error) {
            setStateAlert({
                status: true,
                type: 'error',
                msg: 'Erro ao gerar relatório de inscritos únicos: ' + error.message
            });
        } finally {
            setGenerating(false);
        }
    };

    // Função para gerar relatório de aniversariantes
    const handleGenerateBirthdayReport = async () => {
        try {
            setGenerating(true);
            
            // Buscar pedidos com filtro opcional de evento
            let page = 1;
            const pageSize = 500;
            let allOrders = [];

            while (true) {
                const params = {
                    sort: ['createdAt:desc'],
                    pagination: { page, pageSize },
                    filters: {
                        deleted: { $eq: false },
                        ...(birthdayEvent && {
                            event: { name: { $eq: birthdayEvent.attributes?.name } }
                        })
                    },
                    populate: {
                        event: { fields: ['name'] },
                        subscribers: { fields: ['*'] }
                    },
                    encodeValuesOnly: true
                };

                const queryString = qs.stringify(params, {
                    skipNulls: false,
                    skipUndefined: false,
                    skipEmptyStrings: true
                });

                const response = await api.get(`/orders?${queryString}`);
                const ordersData = response.data.data;

                if (!ordersData || ordersData.length === 0) break;

                allOrders.push(...ordersData);
                page++;
            }

            const uniqueSubscribers = new Map();

            // Coletar todos os inscritos únicos
            allOrders.forEach(order => {
                const subscribers = order.attributes.subscribers?.data || [];
                
                subscribers.forEach(subscriber => {
                    const cpf = subscriber.attributes?.document;
                    if (cpf && !uniqueSubscribers.has(cpf)) {
                        uniqueSubscribers.set(cpf, {
                            name: subscriber.attributes?.name,
                            gender: subscriber.attributes?.gender,
                            birth: subscriber.attributes?.birth,
                            email: subscriber.attributes?.email,
                            whatsapp: subscriber.attributes?.whatsapp,
                            city: subscriber.attributes?.city,
                            state: subscriber.attributes?.state
                        });
                    }
                });
            });

            // Filtrar aniversariantes do período
            const currentDate = new Date(birthdayDate);
            const currentMonth = currentDate.getMonth();
            const currentDay = currentDate.getDate();
            const currentYear = currentDate.getFullYear();

            let filteredBirthdays = [];

            Array.from(uniqueSubscribers.values()).forEach(subscriber => {
                if (!subscriber.birth) return;

                const birthDate = new Date(subscriber.birth);
                const birthMonth = birthDate.getMonth();
                const birthDay = birthDate.getDate();

                let include = false;

                if (birthdayPeriod === 'DIARIA') {
                    // Aniversariantes do dia específico
                    include = birthMonth === currentMonth && birthDay === currentDay;
                } else if (birthdayPeriod === 'SEMANAL') {
                    // Aniversariantes da semana (7 dias a partir da data selecionada)
                    const weekEnd = new Date(currentYear, currentMonth, currentDay + 6);
                    const weekEndMonth = weekEnd.getMonth();
                    const weekEndDay = weekEnd.getDate();
                    
                    if (currentMonth === weekEndMonth) {
                        // Mesma semana, mesmo mês
                        include = birthMonth === currentMonth && birthDay >= currentDay && birthDay <= weekEndDay;
                    } else {
                        // Semana atravessa meses
                        include = (birthMonth === currentMonth && birthDay >= currentDay) ||
                                  (birthMonth === weekEndMonth && birthDay <= weekEndDay);
                    }
                } else if (birthdayPeriod === 'MENSAL') {
                    // Aniversariantes do mês
                    include = birthMonth === currentMonth;
                }

                if (include) {
                    filteredBirthdays.push({
                        ...subscriber,
                        birthDay,
                        birthMonth
                    });
                }
            });

            if (filteredBirthdays.length === 0) {
                setStateAlert({
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum aniversariante encontrado no período selecionado!'
                });
                return;
            }

            // Ordenar por data (dia/mês) e depois por sexo
            filteredBirthdays.sort((a, b) => {
                // Primeiro por dia
                if (a.birthDay !== b.birthDay) {
                    return a.birthDay - b.birthDay;
                }
                // Se mesmo dia, ordenar por sexo (Feminino antes)
                if (a.gender === 'Feminino' && b.gender !== 'Feminino') return -1;
                if (a.gender !== 'Feminino' && b.gender === 'Feminino') return 1;
                return 0;
            });

            // Transformar dados para Excel
            const excelData = filteredBirthdays.map(sub => {
                const birthDateValue = sub.birth ? parseISO(sub.birth) : null;

                return {
                    'Dia': `${String(sub.birthDay).padStart(2, '0')}/${String(sub.birthMonth + 1).padStart(2, '0')}`,
                    'Nome': sub.name || '',
                    'Sexo': sub.gender || '',
                    'Data Nascimento': birthDateValue,
                    'Email': sub.email || '',
                    'Celular': sub.whatsapp || '',
                    'Cidade': sub.city || '',
                    'UF': sub.state || ''
                };
            });

            // Gera Excel
            const sheet = XLSX.utils.json_to_sheet(excelData, { cellDates: true });

            for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
                const birthCellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: 3 });
                const birthCell = sheet[birthCellAddress];

                if (birthCell?.v instanceof Date) {
                    birthCell.z = 'dd/mm/yyyy';
                }
            }
            
            const columnWidths = [
                { wch: 8 },  // Dia
                { wch: 30 }, // Nome
                { wch: 10 }, // Sexo
                { wch: 15 }, // Data Nascimento
                { wch: 30 }, // Email
                { wch: 18 }, // Celular
                { wch: 20 }, // Cidade
                { wch: 6 }   // UF
            ];
            
            sheet['!cols'] = columnWidths;

            const workBook = XLSX.utils.book_new();
            
            let sheetName = '';
            const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                                'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
            
            if (birthdayPeriod === 'DIARIA') {
                sheetName = `Aniver ${currentDay}-${currentMonth + 1}`;
            } else if (birthdayPeriod === 'SEMANAL') {
                sheetName = `Aniver Semana ${currentDay}-${currentMonth + 1}`;
            } else {
                sheetName = `Aniver ${monthNames[currentMonth]}`;
            }
            
            XLSX.utils.book_append_sheet(workBook, sheet, sheetName);

            const fileName = `aniversariantes-${birthdayPeriod.toLowerCase()}-${new Date().toISOString().split('T')[0]}.xlsx`;
            await XLSX.writeFile(workBook, fileName);

            setStateAlert({
                status: true,
                type: 'success',
                msg: `Relatório de aniversariantes gerado! ${filteredBirthdays.length} aniversariantes encontrados.`
            });

        } catch (error) {
            setStateAlert({
                status: true,
                type: 'error',
                msg: 'Erro ao gerar relatório de aniversariantes: ' + error.message
            });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100vh' }} p={3}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ ...stateAlert, status: false })}
            >
                <Alert severity={stateAlert.type}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" color="primary">
                    <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Relatórios Personalizados
                </Typography>
                
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={handleGenerateUniqueSubscribersReport}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        px: 3
                    }}
                >
                    Relatório de Inscritos Únicos
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Painel de Configuração */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardHeader title="Configuração do Relatório" />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nome do Relatório"
                                        value={reportName}
                                        onChange={(e) => setReportName(e.target.value)}
                                        placeholder="Ex: Relatório de Inscrições 2024"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={eventOptions}
                                        loading={loadingEvents}
                                        getOptionLabel={(option) => option.attributes?.name || ''}
                                        value={selectedEvents}
                                        onChange={(event, newValue) => setSelectedEvents(newValue)}
                                        onInputChange={(_, value, reason) => {
                                            if (reason === 'input') {
                                                setEventSearchText(value);
                                                clearTimeout(eventSearchTimer.current);
                                                eventSearchTimer.current = setTimeout(() => {
                                                    searchEventsByText(value);
                                                }, 800);
                                            }
                                        }}
                                        filterOptions={(options) => options}
                                        ListboxProps={{
                                            ref: listboxRef,
                                            onScroll: (event) => {
                                                const listbox = event.currentTarget;
                                                scrollPositionRef.current = listbox.scrollTop;

                                                if (
                                                    listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10 &&
                                                    hasMoreEvents &&
                                                    !loadingEvents
                                                ) {
                                                    loadMoreEvents();
                                                }
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Eventos"
                                                placeholder="Selecione os eventos"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {loadingEvents ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    label={option.attributes?.name || ''}
                                                    {...getTagProps({ index })}
                                                />
                                            ))
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={statusOptions}
                                        value={selectedStatus}
                                        onChange={(event, newValue) => setSelectedStatus(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Status"
                                                placeholder="Selecione os status"
                                            />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                />
                                            ))
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Data Início"
                                        value={dateRange.start}
                                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Data Fim"
                                        value={dateRange.end}
                                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Seção de Relatório de Aniversariantes */}
                            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Relatório de Aniversariantes
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            options={eventOptions}
                                            loading={loadingEvents}
                                            getOptionLabel={(option) => option.attributes?.name || ''}
                                            value={birthdayEvent}
                                            onChange={(event, newValue) => setBirthdayEvent(newValue)}
                                            onInputChange={(_, value, reason) => {
                                                if (reason === 'input') {
                                                    setEventSearchText(value);
                                                    clearTimeout(eventSearchTimer.current);
                                                    eventSearchTimer.current = setTimeout(() => {
                                                        searchEventsByText(value);
                                                    }, 800);
                                                }
                                            }}
                                            filterOptions={(options) => options}
                                            ListboxProps={{
                                                ref: listboxRef,
                                                onScroll: (event) => {
                                                    const listbox = event.currentTarget;
                                                    scrollPositionRef.current = listbox.scrollTop;

                                                    if (
                                                        listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10 &&
                                                        hasMoreEvents &&
                                                        !loadingEvents
                                                    ) {
                                                        loadMoreEvents();
                                                    }
                                                },
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Evento (Opcional)"
                                                    placeholder="Todos os eventos"
                                                    helperText="Deixe vazio para incluir todos os eventos"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <React.Fragment>
                                                                {loadingEvents ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </React.Fragment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                                                Período
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                value={birthdayPeriod}
                                                onChange={(e) => setBirthdayPeriod(e.target.value)}
                                            >
                                                <FormControlLabel 
                                                    value="DIARIA" 
                                                    control={<Radio size="small" />} 
                                                    label="Diária" 
                                                />
                                                <FormControlLabel 
                                                    value="SEMANAL" 
                                                    control={<Radio size="small" />} 
                                                    label="Semanal" 
                                                />
                                                <FormControlLabel 
                                                    value="MENSAL" 
                                                    control={<Radio size="small" />} 
                                                    label="Mensal" 
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Data de Referência"
                                            value={birthdayDate}
                                            onChange={(e) => setBirthdayDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            helperText="Data base para calcular o período"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<Download />}
                                            onClick={handleGenerateBirthdayReport}
                                            disabled={generating}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontSize: '0.95rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            Gerar Relatório de Aniversariantes
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Templates Pré-definidos */}
                    <Card sx={{ mt: 2 }}>
                        <CardHeader title="Templates Prontos" />
                        <CardContent>
                            <Grid container spacing={1}>
                                {Object.entries(templates).map(([key, template]) => (
                                    <Grid item xs={12} key={key}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => handleLoadTemplate(key)}
                                            sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                        >
                                            {template.name}
                                        </Button>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="warning"
                                        onClick={handleClearFields}
                                        disabled={selectedFields.length === 0}
                                    >
                                        Limpar Campos
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Campos Disponíveis */}
                    <Card sx={{ mt: 2 }}>
                        <CardHeader title="Campos Disponíveis" />
                        <CardContent sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {Object.entries(AVAILABLE_FIELDS).map(([categoryKey, category]) => (
                                <Box key={categoryKey} mb={2}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        {category.title}
                                    </Typography>
                                    {Object.entries(category.fields).map(([fieldKey, field]) => (
                                        <Box key={fieldKey} display="flex" alignItems="center" mb={1}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleAddField(categoryKey, fieldKey)}
                                                disabled={selectedFields.some(f => f.id === `${categoryKey}_${fieldKey}`)}
                                            >
                                                <Add />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ ml: 1 }}>
                                                {field.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Divider sx={{ my: 1 }} />
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Painel de Campos Selecionados */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardHeader
                            title={`Campos Selecionados (${selectedFields.length})`}
                            action={
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={handlePreview}
                                        disabled={generating || selectedFields.filter(f => f.enabled).length === 0}
                                        startIcon={<Assessment />}
                                        sx={{ mr: 1 }}
                                    >
                                        {generating ? 'Gerando...' : 'Preview'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handleGenerateExcel}
                                        disabled={generating || selectedFields.filter(f => f.enabled).length === 0}
                                        startIcon={generating ? <CircularProgress size={20} /> : <FileDownload />}
                                    >
                                        {generating ? 'Gerando...' : 'Gerar Excel'}
                                    </Button>
                                </Box>
                            }
                        />
                        <CardContent>
                            {selectedFields.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
                                    Nenhum campo selecionado. Use os botões "+" à esquerda para adicionar campos.
                                </Typography>
                            ) : (
                                <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                                    {selectedFields.map((field, index) => (
                                        <ListItem
                                            key={field.id}
                                            sx={{
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                mb: 1,
                                                bgcolor: 'white',
                                                opacity: field.enabled ? 1 : 0.5
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Box display="flex" flexDirection="column">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveUp(index)}
                                                        disabled={index === 0}
                                                    >
                                                        <ArrowUpward fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveDown(index)}
                                                        disabled={index === selectedFields.length - 1}
                                                    >
                                                        <ArrowDownward fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={field.label}
                                                secondary={`${AVAILABLE_FIELDS[field.category].title} • ${field.type}`}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.enabled}
                                                        onChange={() => handleToggleField(field.id)}
                                                    />
                                                }
                                                label=""
                                            />
                                            <IconButton
                                                onClick={() => handleRemoveField(field.id)}
                                                size="small"
                                                color="error"
                                            >
                                                <Remove />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Dialog de Preview */}
            <Dialog
                open={previewDialog}
                onClose={() => setPreviewDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Preview do Relatório (Primeiras 10 linhas)</DialogTitle>
                <DialogContent>
                    {previewData.length > 0 ? (
                        <Box sx={{ overflow: 'auto', maxHeight: 400 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {Object.keys(previewData[0]).map(key => (
                                            <th key={key} style={{
                                                border: '1px solid #ccc',
                                                padding: '8px',
                                                backgroundColor: '#f5f5f5',
                                                fontWeight: 'bold'
                                            }}>
                                                {key}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, index) => (
                                        <tr key={index}>
                                            {Object.values(row).map((value, i) => (
                                                <td key={i} style={{
                                                    border: '1px solid #ccc',
                                                    padding: '8px',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    {value}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    ) : (
                        <Typography>Nenhum dado encontrado</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}